import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import LiveVideoPlayer from '../video/LiveVideoPlayer.jsx';
import DisplayWallGridControl from './DisplayWallGridControl.jsx';

const resolveCsrfToken = () => {
    if (typeof document === 'undefined') {
        return null;
    }
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : null;
};

const notify = (message, type = 'info', icon = 'info') => {
    const $ = window.jQuery || window.$;
    if ($ && typeof $.notify === 'function') {
        $.notify({ icon, message }, {
            type,
            timer: 4000,
            placement: { from: 'top', align: 'right' },
        });
    } else {
        if (type === 'danger') {
            console.error(message);
        } else if (type === 'success') {
            console.info(message);
        } else {
            console.log(message);
        }
    }
};

const DisplayWallViewer = ({ displayWall, commands, streams, fluxes, links, permissions, primaryStream }) => {
    const [activeAction, setActiveAction] = useState(null);
    const [remoteFullscreenOrder, setRemoteFullscreenOrder] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);

    const csrfToken = useMemo(() => resolveCsrfToken(), []);

    useEffect(() => {
        if (!commands.keepAliveUrl) {
            return;
        }

        const keepAlive = async () => {
            try {
                const response = await fetch(commands.keepAliveUrl, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                    },
                });
                if (!response.ok) {
                    console.warn('Keepalive failed', response.status);
                }
            } catch (error) {
                console.warn('Keepalive error', error);
            }
        };

        // Initial call
        keepAlive();

        // Set interval for every 3 minutes
        const interval = setInterval(keepAlive, 180000);

        return () => clearInterval(interval);
    }, [commands.keepAliveUrl, csrfToken]);

    const sendCommand = useCallback(async (url, payload, actionKey, successMessage, errorFallback) => {
        if (!url) {
            notify(errorFallback ?? 'Missing command endpoint.', 'danger', 'error');
            return false;
        }

        setActiveAction(actionKey);

        if (typeof window !== 'undefined' && window.console) {
            console.info('[DisplayWall] Dispatching command', {
                url,
                payload,
                actionKey,
            });
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                },
                body: JSON.stringify(payload ?? {}),
            });

            const text = await response.text();
            let data = {};
            if (text) {
                try {
                    data = JSON.parse(text);
                } catch (parseError) {
                    data = { message: text };
                }
            }

            if (!response.ok) {
                if (typeof window !== 'undefined' && window.console) {
                    console.warn('[DisplayWall] Command failed', {
                        url,
                        payload,
                        status: response.status,
                        body: data,
                    });
                }
                notify(data.message || errorFallback || 'Command failed.', 'danger', 'error');
                return false;
            }

            if (typeof window !== 'undefined' && window.console) {
                console.info('[DisplayWall] Command acknowledged', {
                    url,
                    payload,
                    status: response.status,
                    body: data,
                });
            }

            notify(data.message || successMessage || 'Command queued.', 'success', 'done');
            return true;
        } catch (error) {
            console.error('Failed to send display wall command', error);
            notify(errorFallback || 'Command failed. Please try again.', 'danger', 'error');
            return false;
        } finally {
            setActiveAction(null);
        }
    }, [csrfToken]);

    const canControl = permissions?.canControl ?? false;
    const isVirtualWall = Boolean(displayWall?.isVirtual);
    const layoutColumns = displayWall?.grid?.columns ?? 0;
    const layoutRows = displayWall?.grid?.rows ?? 0;
    const isWallStarted = displayWall?.lastCommand === 'start';
    const handleStart = useCallback(async () => {
        if (!canControl) {
            return;
        }
        const success = await sendCommand(
            commands.startUrl,
            {},
            'start',
            'Display wall start command queued.',
            'Unable to start the display wall.'
        );
        if (success) {
            setStatusMessage({ type: 'success', text: 'Display wall start command queued.' });
            // Reload to show the player
            setTimeout(() => window.location.reload(), 1000);
        }
    }, [canControl, commands.startUrl, sendCommand]);

    const handleStop = useCallback(async () => {
        if (!canControl) {
            return;
        }
        const success = await sendCommand(
            commands.stopUrl,
            {},
            'stop',
            'Display wall stop command queued.',
            'Unable to stop the display wall.'
        );
        if (success) {
            setStatusMessage({ type: 'info', text: 'Display wall stop command queued.' });
            setRemoteFullscreenOrder(null);
            // Reload to hide the player
            setTimeout(() => window.location.reload(), 2000);
        }
    }, [canControl, commands.stopUrl, sendCommand]);

    const handleFullscreen = useCallback(async (orderIndex, source = 'unknown') => {
        if (!canControl) {
            notify('You do not have permission to control this display wall.', 'danger', 'error');
            return;
        }
        if (orderIndex === undefined || orderIndex === null || Number.isNaN(Number(orderIndex))) {
            notify('Invalid stream selection for fullscreen.', 'danger', 'error');
            return;
        }

        const normalizedOrder = Number(orderIndex);

        if (!streams.some((stream) => stream.order === normalizedOrder)) {
            if (typeof window !== 'undefined' && window.console) {
                console.warn('[DisplayWall] Skipping fullscreen request because no stream exists at the requested index.', {
                    orderIndex: normalizedOrder,
                    source,
                });
            }
            notify('No stream is configured at that position.', 'warning', 'warning');
            return;
        }

        if (typeof window !== 'undefined' && window.console) {
            console.info('[DisplayWall] Requesting fullscreen', {
                orderIndex: normalizedOrder,
                source,
            });
        }

        const success = await sendCommand(
            commands.fullscreenUrl,
            { mode: 'fullscreen', order_index: normalizedOrder },
            `fullscreen-${normalizedOrder}`,
            'Fullscreen command queued. The wall may take a few seconds to update.',
            'Unable to trigger fullscreen.'
        );

        if (success) {
            setRemoteFullscreenOrder(normalizedOrder);
            setStatusMessage({
                type: 'warning',
                text: 'Fullscreen command sent. The wall may take a few seconds to update.',
            });
        }
    }, [canControl, commands.fullscreenUrl, sendCommand, streams]);

    const handleExitFullscreen = useCallback(async () => {
        if (!canControl) {
            return;
        }
        const success = await sendCommand(
            commands.fullscreenUrl,
            { mode: 'normal' },
            'fullscreen-exit',
            'Exit fullscreen command queued.',
            'Unable to exit fullscreen.'
        );

        if (success) {
            setRemoteFullscreenOrder(null);
            setStatusMessage({ type: 'success', text: 'Exit fullscreen command queued.' });
        }
    }, [canControl, commands.fullscreenUrl, sendCommand]);

    const handleGridCellClick = useCallback((row, column) => {
    const columns = (isVirtualWall ? primaryStream?.playerProps?.gridColumns : null) ?? layoutColumns;
        if (!columns) {
            return;
        }
        const zeroBasedOrder = (row - 1) * columns + (column - 1);

        const targetStream = streams.find((stream) => stream.order === zeroBasedOrder);
        if (!targetStream) {
            if (typeof window !== 'undefined' && window.console) {
                console.warn('[DisplayWall] Ignoring grid click with no stream configured.', {
                    row,
                    column,
                    computedOrderIndex: zeroBasedOrder,
                });
            }
            notify('No stream is configured at that grid position yet.', 'warning', 'warning');
            return;
        }

        if (typeof window !== 'undefined' && window.console) {
            console.debug('[DisplayWall] Grid cell click', {
                row,
                column,
                computedOrderIndex: zeroBasedOrder,
            });
        }

        handleFullscreen(zeroBasedOrder, 'grid');
    }, [handleFullscreen, isVirtualWall, layoutColumns, primaryStream, streams]);

    const isGridActive = remoteFullscreenOrder === null;
    const handleBrowserFullscreen = useCallback((event) => {
        const playerRoot = event?.currentTarget?.closest('.live-video-player');
        if (!playerRoot || typeof playerRoot.requestFullscreen !== 'function') {
            return;
        }

        playerRoot.requestFullscreen().catch(() => {});
    }, []);

    const mosaicPlayerProps = useMemo(() => {
        if (!isVirtualWall || !primaryStream?.playerProps) {
            return null;
        }

        const base = primaryStream.playerProps;
        const baseColumns = base.gridColumns ?? layoutColumns;
        const baseRows = base.gridRows ?? layoutRows;

        const exitFullscreenAction = canControl
            ? {
                  key: 'exit-fullscreen',
                  icon: 'fullscreen_exit',
                  label: 'Return to normal view',
                  buttonClassName: 'btn btn-warning btn-sm d-flex align-items-center',
                  onClick: handleExitFullscreen,
                  disabled: activeAction !== null,
                  hint: remoteFullscreenOrder !== null
                      ? `Grid index #${remoteFullscreenOrder} is fullscreen on the wall`
                      : null,
              }
            : null;

        const overlayActions =
            canControl && remoteFullscreenOrder !== null && exitFullscreenAction
                ? [exitFullscreenAction]
                : [];

        const footerActions = [
            {
                key: 'browser-fullscreen',
                icon: 'fullscreen',
                label: 'FullScreen',
                buttonClassName: 'btn btn-sm btn-outline-secondary mt-2',
                onClick: handleBrowserFullscreen,
                disabled: activeAction !== null,
                title: 'Open the wall preview in browser fullscreen',
            },
        ];

        return {
            ...base,
            gridColumns: isGridActive ? Math.max(1, baseColumns || 0) : 0,
            gridRows: isGridActive ? Math.max(1, baseRows || 0) : 0,
            gridDebug: true,
            onGridCellClick: handleGridCellClick,
            overlayActions,
            overlayVisible: remoteFullscreenOrder !== null,
            footerActions,
        };
    }, [
        activeAction,
        canControl,
        handleBrowserFullscreen,
        handleExitFullscreen,
        handleGridCellClick,
        isVirtualWall,
        isGridActive,
        layoutColumns,
        layoutRows,
        primaryStream,
        remoteFullscreenOrder,
    ]);

    const fullscreenButtonVariant = remoteFullscreenOrder !== null ? 'btn-outline-warning' : 'btn-outline-secondary';
    const fullscreenButtonIcon = remoteFullscreenOrder !== null ? 'fullscreen_exit' : 'crop_free';
    const fullscreenButtonTitle = remoteFullscreenOrder !== null
        ? 'We think the wall is in fullscreen. Click to return to normal view.'
        : 'Return wall to mosaic layout.';

    const normalizedColumns = Math.max(1, layoutColumns || 0);
    const normalizedRows = Math.max(1, layoutRows || 0);
    const shouldRenderGridControl = ((!isVirtualWall || !primaryStream) && streams.length > 0);

    return (
        <div className="row">
            <div className="col-lg-8 col-xl-9 mb-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                        <h3 className="mb-1">{displayWall?.name}</h3>
                        <div className="text-muted small">
                            {displayWall?.device?.name || 'Unknown device'} · {displayWall?.grid?.columns || 0} × {displayWall?.grid?.rows || 0}
                        </div>
                    </div>
                    <div className="btn-group" role="group" aria-label="Display wall commands">
                        {canControl && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={handleStart}
                                    disabled={activeAction !== null}
                                >
                                    <i className="material-icons mr-1">play_circle</i> Start display
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-warning btn-sm"
                                    onClick={handleStop}
                                    disabled={activeAction !== null}
                                >
                                    <i className="material-icons mr-1">stop_circle</i> Stop display
                                </button>
                            </>
                        )}
                        {canControl && (
                            <button
                                type="button"
                                className={`btn btn-sm ${fullscreenButtonVariant}`}
                                onClick={handleExitFullscreen}
                                disabled={activeAction !== null}
                                title={fullscreenButtonTitle}
                            >
                                <i className="material-icons mr-1">{fullscreenButtonIcon}</i>
                                Return to normal view
                            </button>
                        )}
                        <a href={(links && links.index) || '/display-walls'} className="btn btn-outline-secondary btn-sm">
                            <i className="material-icons mr-1">arrow_back</i> Back
                        </a>
                    </div>
                </div>

                {statusMessage && (
                    <div className={`alert alert-${statusMessage.type} mb-3`}>
                        {statusMessage.text}
                    </div>
                )}

                {streams.length === 0 && (
                    <div className="alert alert-warning" role="alert">
                        This display wall has no streams configured yet.
                    </div>
                )}

                {!isWallStarted && (
                    <div className="card mb-4">
                        <div className="card-body text-center">
                            <div className="mb-3">
                                <i className="material-icons" style={{ fontSize: '4rem', color: '#ccc' }}>stop_circle</i>
                            </div>
                            <h5 className="card-title">Display Wall is Stopped</h5>
                            <p className="card-text text-muted">
                                The display wall is currently stopped. Press the start button to begin displaying content.
                            </p>
                            {canControl && (
                                <button
                                    type="button"
                                    className="btn btn-success btn-lg"
                                    onClick={handleStart}
                                    disabled={activeAction !== null}
                                >
                                    <i className="material-icons mr-2">play_circle</i>
                                    Start Display Wall
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {isWallStarted && (
                    <>
                        {!isVirtualWall && streams.length > 0 && (
                            <div className="alert alert-info" role="alert">
                                This physical wall does not expose a live mosaic preview. Use the grid control below to trigger fullscreen on the device.
                            </div>
                        )}

                        {isVirtualWall && !primaryStream && streams.length > 0 && (
                            <div className="alert alert-info" role="alert">
                                The mosaic feed isn&apos;t available yet. Use the grid control below to trigger fullscreen remotely.
                            </div>
                        )}

                        {isVirtualWall && primaryStream && mosaicPlayerProps && (
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0">{primaryStream.title}</h5>
                                    <div className="text-muted small">Hover the player to reveal the wall grid and click a cell to request fullscreen.</div>
                                </div>
                                <div className="card-body">
                                    <div className="display-wall-player-container">
                                        <LiveVideoPlayer {...mosaicPlayerProps} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {shouldRenderGridControl && (
                            <DisplayWallGridControl
                                columns={normalizedColumns}
                                rows={normalizedRows}
                                streams={streams}
                                onCellFullscreen={handleFullscreen}
                                activeOrder={remoteFullscreenOrder}
                                busy={activeAction !== null}
                            />
                        )}
                    </>
                )}

            </div>
            <div className="col-lg-4 col-xl-3 mb-4">
                <div className="card">
                    <div className="card-header card-header-rose">
                        <h5 className="card-title mb-0">Configured streams</h5>
                        <small className="d-block text-white-50">Use the list or grid overlay to drive fullscreen.</small>
                    </div>
                    <div className="card-body p-0">
                        {streams.length === 0 ? (
                            <p className="p-3 text-muted mb-0">No streams are configured for this wall.</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {streams.map((stream) => (
                                    <li className="list-group-item d-flex justify-content-between align-items-center" key={stream.id || stream.order}>
                                        <div>
                                            <div className="font-weight-bold">{stream.title}</div>
                                            <div className="small text-muted" style={{overflowWrap:"anywhere"}}>
                                                {stream.typeLabel} · Index #{stream.order+1}
                                                {stream.flux && (
                                                    <>
                                                        {' · '}Flux #{stream.flux.id}: {stream.flux.name}                                                      
                                                    </>
                                                )}<br />
                                                {stream.resolvedUrl}
                                            </div>
                                        </div>
                                        {canControl && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-light btn-sm"
                                                onClick={() => handleFullscreen(stream.order, 'list')}
                                                disabled={activeAction !== null}
                                            >
                                                <i className="material-icons mr-1">present_to_all</i>
                                                Fullscreen
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {remoteFullscreenOrder !== null && canControl && (
                        <div className="card-footer text-center">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleExitFullscreen}
                                disabled={activeAction !== null}
                            >
                                <i className="material-icons mr-1">close_fullscreen</i>
                                Exit remote fullscreen
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

DisplayWallViewer.propTypes = {
    displayWall: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
        isVirtual: PropTypes.bool,
        lastCommand: PropTypes.string,
        lastCommandSub: PropTypes.string,
        grid: PropTypes.shape({
            columns: PropTypes.number,
            rows: PropTypes.number,
        }).isRequired,
        device: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            clientId: PropTypes.number,
        }),
    }).isRequired,
    commands: PropTypes.shape({
        startUrl: PropTypes.string,
        stopUrl: PropTypes.string,
        fullscreenUrl: PropTypes.string,
        keepAliveUrl: PropTypes.string,
    }).isRequired,
    streams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string.isRequired,
        typeLabel: PropTypes.string.isRequired,
        position: PropTypes.number,
        order: PropTypes.number.isRequired,
        resolvedUrl: PropTypes.string,
        playerProps: PropTypes.object.isRequired,
        flux: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
    })).isRequired,
    fluxes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
    })).isRequired,
    links: PropTypes.shape({
        index: PropTypes.string,
    }),
    permissions: PropTypes.shape({
        canControl: PropTypes.bool,
    }),
    primaryStream: PropTypes.shape({
        title: PropTypes.string.isRequired,
        playerProps: PropTypes.object.isRequired,
    }),
};

DisplayWallViewer.defaultProps = {
    links: {},
    permissions: { canControl: false },
    primaryStream: null,
};

export default DisplayWallViewer;
